import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useTerritories } from "@/api/commerce";

import { downloadExport, useAccounts } from "@/api/crm";
import { useMe } from "@/api/hooks";
import { ACCOUNT_TYPES } from "@/api/types";
import { Badge } from "@/components/crm/Badge";
import { Icon } from "@/components/icons";
import { Alert, Avatar, EmptyState, Modal, PageHeader, Spinner } from "@/components/ui";
import { titleCase } from "@/lib/format";
import { can } from "@/lib/permissions";
import { AccountForm } from "./AccountForm";

export function AccountsPage() {
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const perms = useMe().data?.permissions ?? [];
  const [params, setParams] = useSearchParams();
  const territory = params.get("territory") ?? "";
  const territories = useTerritories();
  const accounts = useAccounts(q, type, territory);
  const rows = accounts.data?.pages.flatMap((p) => p.items) ?? [];
  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 250); return () => clearTimeout(t); }, [search]);

  const newButton = can(perms, "accounts:create") && <button type="button" className="btn-primary" onClick={() => setCreating(true)}><Icon name="plus" />New account</button>;
  const exportButton = can(perms, "data:export") && <button type="button" className="btn-ghost" onClick={() => void downloadExport("account")}><Icon name="download" />Export</button>;

  return (
    <>
      <PageHeader title="Accounts" subtitle="Customers, prospects, dealers, distributors and suppliers." actions={<>{exportButton}{newButton}</>} />
      <div className="mb-4 flex flex-wrap gap-2">
        <input type="search" className="input max-w-sm" placeholder="Search name, city, tax ID or phone" aria-label="Search accounts" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="input w-auto" aria-label="Account type" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All types</option>
          {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
        </select>
        {!!territories.data?.length && (
          <select className="input w-auto" aria-label="Territory" value={territory} onChange={(e) => setParams(e.target.value ? { territory: e.target.value } : {})}>
            <option value="">All territories</option>
            {territories.data.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </div>
      {accounts.isError && <Alert>{accounts.error.message}</Alert>}
      {accounts.isLoading && <Spinner />}
      {accounts.isSuccess && rows.length === 0 && (
        q || type ? <EmptyState title="No matches" body="No accounts match these filters." />
          : <EmptyState title="No accounts yet" body="Add the companies you sell to, or import them from a spreadsheet." action={newButton} />
      )}
      {rows.length > 0 && (
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-canvas text-xs uppercase tracking-wide text-muted">
              <tr><th className="px-4 py-3">Account</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Location</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Owner</th></tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="cursor-pointer border-b border-line last:border-0 hover:bg-canvas" onClick={() => navigate(`/accounts/${a.id}`)}>
                  <td className="px-4 py-3">
                    <Link to={`/accounts/${a.id}`} className="flex items-center gap-3 font-medium hover:text-brand" onClick={(e) => e.stopPropagation()}>
                      <Avatar name={a.name} />
                      <span>{a.name}<span className="block text-xs font-normal text-muted">{a.industry}</span></span>
                    </Link>
                  </td>
                  <td className="px-4 py-3"><Badge tone={a.account_type === "customer" ? "ok" : a.account_type === "prospect" ? "neutral" : "brand"}>{titleCase(a.account_type)}</Badge></td>
                  <td className="px-4 py-3 text-muted">{[a.city, a.state].filter(Boolean).join(", ")}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted">{a.phone}</td>
                  <td className="whitespace-nowrap px-4 py-3">{a.owner?.full_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {accounts.hasNextPage && (
        <div className="mt-4 text-center"><button type="button" className="btn-ghost" onClick={() => void accounts.fetchNextPage()}>Load more</button></div>
      )}
      {creating && (
        <Modal title="New account" onClose={() => setCreating(false)}>
          <AccountForm onCancel={() => setCreating(false)} onSaved={(a) => { setCreating(false); navigate(`/accounts/${a.id}`); }} />
        </Modal>
      )}
    </>
  );
}
